import React from 'react'

import {ListItem, Panel} from '../UI/surfaces/index.js'
import {H1, S2} from '../UI/typography/index.js'
import type {PopupInput} from '../UI/overlays/PopupInputs.js'
import {labelSheets, labelStartPickerStyle} from './styles.js'

interface LabelStartPickerProps {
    /** The printer page size of the print, only the A4 sheets with several labels have a picker */
    printerPageSize: string
    /** The number of labels to leave empty: the position of the first label minus one */
    value: number
    onChange: (nrOfEmptyLabels: number) => void
    disabled?: boolean
}

/**
 * Chooses where on a partly used A4 sheet the first label goes: the sheet as a grid of its labels, the ones before
 * the chosen one are grey. Renders nothing for a page size with one label per page.
 */
export default function LabelStartPicker({printerPageSize, value, onChange, disabled}: LabelStartPickerProps) {
    const sheet = labelSheets[printerPageSize]

    if (!sheet) {
        return null
    }

    const size = sheet.portrait ? labelStartPickerStyle.label.portrait : labelStartPickerStyle.label.landscape

    return (
        <div style={{paddingTop: labelStartPickerStyle.paddingTop, marginBottom: labelStartPickerStyle.marginBottom}}>
            <S2 style={{marginBottom: labelStartPickerStyle.titleGap}}>Kies de positie van het eerste label:</S2>

            <div style={{display: 'flex', justifyContent: 'center'}}>
                <Panel style={{display: 'flex', flexWrap: 'wrap', maxWidth: sheet.maxWidth}}>
                    {Array.from({length: sheet.labels}, (_, position) => (
                        <ListItem
                            key={position}
                            style={{...size, justifyContent: 'center'}}
                            wrapperStyle={size}
                            inactive={value > position}
                            // Not ListItem's disabled: that greys out the whole sheet
                            onClick={disabled ? undefined : () => onChange(position)}
                        >
                            {/* No colour of its own: grey with the inactive item */}
                            <H1 style={{color: ''}}>{position + 1}</H1>
                        </ListItem>
                    ))}
                </Panel>
            </div>
        </div>
    )
}

/** The Popup input of the picker (confirmed as its value), none for a page size with one label per page */
export function labelStartInput(printerPageSize: string, nrOfEmptyLabels = 0): PopupInput | undefined {
    if (!labelSheets[printerPageSize]) {
        return undefined
    }

    return {
        type: 'custom',
        name: 'nrOfEmptyLabels',
        value: nrOfEmptyLabels,
        render: (value, onChange, {disabled}) => (
            <LabelStartPicker printerPageSize={printerPageSize} value={value} onChange={onChange} disabled={disabled} />
        )
    }
}
